import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Upload, FileText, X, Brain, Loader2, History, Target } from 'lucide-react';
import { api } from '../../../utils/api';

// Dialog för att starta AI-materialberäkning. Ren presentation — state styrs av parent.
const MaterialPlanDialog = ({
  open, onOpenChange, form, setForm, files, fileInputRef,
  onFileChange, onRemoveFile, onGenerate, generating,
}) => {
  const [recentNotes, setRecentNotes] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.listRecentMaterialNotes()
      .then(res => setRecentNotes(res.data?.recent_notes || []))
      .catch(() => setRecentNotes([]));
    api.listMaterialProjectTypes()
      .then(res => setProjectTypes(res.data?.project_types || []))
      .catch(() => setProjectTypes([]));
  }, [open]);

  const useRecent = (n) => {
    setForm((f) => ({
      ...f,
      notes: n.notes || '',
      job_type: f.job_type || n.job_type || '',
    }));
  };

  // Live benchmark-preview baserat på val
  const selectedBm = projectTypes.find(pt => pt.key === form.project_type);
  let bmPreview = null;
  if (selectedBm && form.total_area_m2 && parseFloat(form.total_area_m2) > 0) {
    const area = parseFloat(form.total_area_m2);
    const minTotal = selectedBm.total_min_sek_per_m2 * area;
    const maxTotal = selectedBm.total_max_sek_per_m2 * area;
    const matMin = Math.round(minTotal * 0.35);
    const matMax = Math.round(maxTotal * 0.45);
    bmPreview = { minTotal, maxTotal, matMin, matMax };
  }

  const fmtMsek = (n) => `${(n/1_000_000).toLocaleString('sv-SE',{maximumFractionDigits:1})} MSEK`;

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl" data-testid="material-dialog">
      <DialogHeader>
        <DialogTitle>Beräkna material med AI</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
        {/* Projekttyp + yta för benchmark-validering */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Projekttyp *</Label>
            <select
              value={form.project_type || ''}
              onChange={(e) => setForm((f) => ({ ...f, project_type: e.target.value }))}
              className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
              data-testid="material-project-type"
            >
              <option value="">— välj typ —</option>
              {projectTypes.map(pt => (
                <option key={pt.key} value={pt.key}>{pt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Yta BTA (m²) *</Label>
            <Input
              type="number"
              value={form.total_area_m2 || ''}
              onChange={(e) => setForm((f) => ({ ...f, total_area_m2: e.target.value }))}
              placeholder="9300"
              data-testid="material-area"
            />
          </div>
        </div>

        {bmPreview && (
          <div className="rounded-md bg-[hsl(16,100%,55%)]/8 border border-[hsl(16,100%,55%)]/30 p-2.5 text-xs" data-testid="benchmark-preview">
            <div className="flex items-start gap-2">
              <Target size={14} className="text-[hsl(16,100%,55%)] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-foreground">Branschsnitt för denna typ:</div>
                <div className="text-muted-foreground">Total byggkostnad: {fmtMsek(bmPreview.minTotal)} – {fmtMsek(bmPreview.maxTotal)}</div>
                <div className="text-foreground font-medium">Förväntad materialkostnad: {fmtMsek(bmPreview.matMin)} – {fmtMsek(bmPreview.matMax)}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <Label>Typ av jobb / specifika arbeten *</Label>
          <Input
            value={form.job_type}
            onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))}
            placeholder="T.ex. Nybyggnad F-6 skola, Fasadrenovering, Schaktarbete..."
            data-testid="material-job-type"
          />
        </div>
        <div>
          <Label>Våningar (valfritt)</Label>
          <Input type="number" value={form.floors || ''} onChange={(e) => setForm((f) => ({ ...f, floors: e.target.value }))} placeholder="2" data-testid="material-floors" />
          <p className="text-[10px] text-muted-foreground mt-1">Timmar, arbetare och maskiner räknar AI fram åt dig i Arbete-steget.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Beskrivning / Krav</Label>
            {recentNotes.length > 0 && (
              <div className="flex items-center gap-1.5">
                <History size={12} className="text-muted-foreground" />
                <select
                  onChange={(e) => {
                    const idx = parseInt(e.target.value, 10);
                    if (idx >= 0) useRecent(recentNotes[idx]);
                    e.target.value = '';
                  }}
                  className="text-xs h-7 rounded border border-border bg-background px-2 max-w-[220px]"
                  data-testid="material-reuse-notes"
                  defaultValue=""
                >
                  <option value="">Återanvänd tidigare...</option>
                  {recentNotes.map((n, i) => {
                    const preview = (n.notes || '').slice(0, 55);
                    const label = n.project_name ? `${n.project_name} — ${preview}` : preview;
                    return <option key={n.id} value={i}>{label}{preview.length >= 55 ? '…' : ''}</option>;
                  })}
                </select>
              </div>
            )}
          </div>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Beskriv projektet och ritningarna. Ex: 'F-6-skola 9300 m², 2 plan, 6 hemvister, idrottshall 1400 m², trä- och sär-skola'. Fler detaljer = exaktare kalkyl."
            rows={3}
            data-testid="material-notes"
          />
        </div>
        <div>
          <Label className="mb-2 block">Bifoga ritningar / bilder / specifikationer</Label>
          <div
            className="border-2 border-dashed border-border rounded p-4 text-center cursor-pointer hover:border-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,55%)]/5 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="file-upload-zone"
          >
            <Upload size={24} className="mx-auto text-muted-foreground mb-1" />
            <p className="text-sm text-muted-foreground">Klicka eller dra filer hit</p>
            <p className="text-xs text-muted-foreground/60 mt-0.5">PDF, bilder, ritningar, Excel — AI läser mått direkt från ritningen</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.docx,.csv,.txt"
            className="hidden"
            onChange={onFileChange}
            data-testid="file-input"
          />
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between bg-muted rounded px-3 py-1.5 text-sm" data-testid={`file-${i}`}>
                  <span className="flex items-center gap-1.5 text-foreground truncate">
                    <FileText size={14} className="text-muted-foreground flex-shrink-0" />
                    {f.name}
                  </span>
                  <button onClick={() => onRemoveFile(i)} className="text-muted-foreground hover:text-destructive p-0.5">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
        <Button
          onClick={onGenerate}
          disabled={generating}
          className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white"
          data-testid="submit-material-btn"
        >
          {generating ? (
            <><Loader2 size={14} className="mr-1.5 animate-spin" /> Startar beräkning...</>
          ) : (
            <><Brain size={14} className="mr-1.5" /> Generera materialplan</>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default MaterialPlanDialog;
